import { useState, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { filterWordList } from "../util/wordywhizFilter";
import { getWordDefinition } from "../util/wordywhiz";
import { useAuth } from "../context/AuthContext";

// Matches the CSS rule that rotates `.study-page--flashcard` into a
// landscape-shaped view. Kept in JS too because that rotated view needs
// to escape the route-transition wrapper's `transform` (from the
// page-fade animation) — a `position: fixed` element only measures
// against the real viewport once it's portaled out from under that,
// so we only want to portal while this actually applies.
const FORCE_LANDSCAPE_QUERY = "(orientation: portrait) and (max-width: 768px)";

function useForceLandscape(active) {
  const [shouldForce, setShouldForce] = useState(false);

  useEffect(() => {
    if (!active) { setShouldForce(false); return; }
    const mq = window.matchMedia(FORCE_LANDSCAPE_QUERY);
    const update = () => setShouldForce(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, [active]);

  return shouldForce;
}

function DictionaryStudy() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { dict, userInput } = location.state || {};
  const dictionariesPath = isLoggedIn ? "/wordy-whiz/my-dictionaries" : "/wordy-whiz/dictionaries";

  const wordList = dict
    ? filterWordList(JSON.parse(dict.words_full), userInput)
    : [];
  const readAloud = userInput?.textToSpeech;

  const [currentWord, setCurrentWord] = useState(0);
  const [isStarted, setIsStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [wordInfo, setWordInfo] = useState(null);
  const [speechReady, setSpeechReady] = useState(false);

  // Runs once on mount regardless of `readAloud` at the time — deliberately
  // not re-run if it changes later.
  useEffect(() => {
    if (!readAloud) { setSpeechReady(true); return; }
    const utterance = new SpeechSynthesisUtterance(" ");
    utterance.onend = () => setSpeechReady(true);
    window.speechSynthesis.speak(utterance);
    return () => window.speechSynthesis.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `wordList` is derived fresh every render (not memoized), so it's
  // deliberately excluded here — including it would refetch the
  // definition on every render instead of only when the shown word changes.
  useEffect(() => {
    if (isStarted && isPaused) {
      getWordDefinition(wordList[currentWord]).then(setWordInfo);
    } else {
      setWordInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused, isStarted, currentWord]);

  // `wordList`, `readAloud`, and `userInput.timeBetweenWords` are all
  // derived fresh every render (not memoized) and `endHandler` is
  // recreated every render too — deliberately excluded so this only
  // re-runs on the state transitions it actually cares about.
  useLayoutEffect(() => {
    if (!isStarted || isPaused) return;
    if (currentWord >= wordList.length) { endHandler(); return; }

    if (readAloud) {
      const delay = currentWord === 0 ? 500 : parseInt(userInput.timeBetweenWords) * 1000;
      const timeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(wordList[currentWord]);
        utterance.rate = 0.8;
        utterance.pitch = 1.2;
        utterance.onend = () => setCurrentWord((c) => c + 1);
        window.speechSynthesis.speak(utterance);
      }, delay);
      return () => { clearTimeout(timeout); window.speechSynthesis.cancel(); };
    } else {
      const interval = setInterval(() => {
        setCurrentWord((c) => c + 1);
      }, parseInt(userInput.timeBetweenWords) * 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused, isStarted, currentWord, speechReady]);

  function startHandler() { setIsStarted(true); setIsPaused(false); }
  function pauseHandler() { window.speechSynthesis.cancel(); setIsPaused(true); }
  function nextHandler() {
    // The auto-advance effect's own boundary check only runs while
    // unpaused, so a manual Next on the last word (always clicked while
    // paused) has to check the boundary itself or it'd strand the user
    // on a "Word N of N" card that never ends the session.
    if (currentWord + 1 >= wordList.length) {
      endHandler();
    } else {
      setCurrentWord((c) => c + 1);
    }
  }
  function endHandler() {
    window.speechSynthesis.cancel();
    setCurrentWord(0);
    setIsStarted(false);
    setIsPaused(true);
    navigate(dictionariesPath);
  }

  const forceLandscape = useForceLandscape(!!dict);

  // Learn Mode is meant to be read landscape on a phone — try the real
  // Screen Orientation API (only actually takes effect in an installed/
  // fullscreen context) and let the CSS fallback below handle the rest.
  useEffect(() => {
    if (!dict) return;
    const orientation = window.screen && window.screen.orientation;
    if (orientation && orientation.lock) {
      orientation.lock("landscape").catch(() => {
        // Not supported outside a fullscreen/installed context — the CSS
        // fallback still presents a landscape-shaped view on portrait phones.
      });
    }
    return () => {
      if (orientation && orientation.unlock) {
        try { orientation.unlock(); } catch { /* no-op */ }
      }
    };
  }, [dict]);

  if (!dict) {
    return (
      <div className="study-page">
        <div className="study-header">
          <p className="study-eyebrow">Wordy Whiz · Learn</p>
          <h1>No dictionary selected</h1>
          <p>Pick a dictionary to start a study session.</p>
        </div>
        <div className="dict-config__actions dict-config__actions--center">
          <button className="ww-btn ww-btn--primary" onClick={() => navigate(dictionariesPath)}>
            {isLoggedIn ? "Go to My Dictionaries" : "Browse Dictionaries"}
          </button>
        </div>
      </div>
    );
  }

  const progress = wordList.length > 0 ? ((currentWord) / wordList.length) * 100 : 0;
  const shownWord = isStarted ? wordList[currentWord] : wordList[0];
  const showLoading = !isStarted && isPaused && readAloud && !speechReady;

  let progressLabel, primaryLabel, primaryAction;
  if (!isStarted) {
    progressLabel = "Ready";
    primaryLabel = "Start";
    primaryAction = startHandler;
  } else if (isPaused) {
    progressLabel = `Word ${currentWord + 1} of ${wordList.length}`;
    primaryLabel = "Resume";
    primaryAction = startHandler;
  } else {
    progressLabel = `Word ${currentWord + 1} of ${wordList.length}`;
    primaryLabel = "Pause";
    primaryAction = pauseHandler;
  }

  const session = (
    <div className="study-page study-page--flashcard">
      <div className="study-header">
        <p className="study-eyebrow">Wordy Whiz · {dict.title}</p>
        <h1>Learn Mode</h1>
        {!isStarted && isPaused && (
          <p>
            {wordList.length} word{wordList.length !== 1 ? "s" : ""} ·{" "}
            {userInput.timeBetweenWords}s per word
            {readAloud ? " · Read aloud" : ""}
          </p>
        )}
      </div>

      <div className="flashcard">
        {showLoading ? (
          <>
            <p className="ww-label ww-label--on-walnut">Preparing audio...</p>
            <div className="study-spinner" />
          </>
        ) : (
          <>
            <div className="flashcard__tiles">
              {shownWord.split("").map((ch, i) => (
                <span key={`${ch}-${i}`} className="ww-tile ww-tile--cream">{ch}</span>
              ))}
            </div>

            <p className="ww-label ww-label--on-walnut">{progressLabel}</p>

            {isStarted && isPaused && wordInfo && (
              <div className="flashcard__meta">
                <p className="flashcard__pos">
                  {[wordInfo.partOfSpeech, wordInfo.pronunciation].filter(Boolean).join(" · ")}
                </p>
                <p className="flashcard__def">{wordInfo.definition}</p>
                {wordInfo.example && <p className="flashcard__example">“{wordInfo.example}”</p>}
              </div>
            )}
            {isStarted && !isPaused && (
              <p className="flashcard__hint ww-label ww-label--on-walnut">Pause to see the definition</p>
            )}

            <div className="flashcard__progress">
              <div className="flashcard__progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <div className="flashcard__controls">
              <button className="ww-btn ww-btn--secondary" onClick={endHandler}>Back</button>
              <button className="ww-btn ww-btn--primary" onClick={primaryAction}>{primaryLabel}</button>
              {isStarted && isPaused && (
                <button className="ww-btn ww-btn--accent" onClick={nextHandler}>Next</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Portaled straight to <body> so the forced-landscape rotation measures
  // against the real viewport instead of the route-transition wrapper.
  return forceLandscape ? createPortal(session, document.body) : session;
}

export default DictionaryStudy;
